import { Maximize2 } from 'lucide-react';

import { cn } from '@/lib/cn';
import {
  formatCountdown,
  useCountdown,
  useFocus,
  useFocusSessionSync,
} from './useFocus';
import { formatClock, useElapsed } from './useTimer';
import { useWorkBlock } from './useWorkBlocks';

const PHASE_SHORT: Record<string, string> = {
  planning: 'Planning',
  sprint: 'Sprint',
  break_pending: 'Break?',
  break: 'Break',
  return_pending: 'Return',
  wind_down: 'Wind down',
  review: 'Review',
};

interface Props {
  blockId: number;
  onExpand: () => void;
}

/** Minimized work block: timers keep running while browsing the app. */
export function WorkBlockBar({ blockId, onExpand }: Props) {
  const block = useWorkBlock(blockId);
  const focus = useFocus();

  const endsAt =
    block.data?.phase === 'planning'
      ? block.data.planning_ends_at
      : block.data?.phase === 'wind_down'
        ? block.data.wind_down_ends_at
        : focus.data?.session_ends_at ?? null;

  const remaining = useCountdown(endsAt);
  const taskElapsed = useElapsed(focus.data?.task_started_at ?? null);
  // Past the bell the sprint keeps running, so show the overage rather than a
  // countdown parked at zero.
  const overage = useElapsed(
    block.data?.phase === 'break_pending' ? focus.data?.session_ends_at ?? null : null,
  );

  useFocusSessionSync(endsAt, remaining, () => {
    void block.refetch();
    void focus.refetch();
  });

  if (!block.data || !focus.data) return null;

  const phase = block.data.phase;
  const showOverage = phase === 'break_pending' && Boolean(focus.data.session_ends_at);
  const title =
    focus.data.active_task_title ??
    focus.data.active_calendar_event_title ??
    (focus.data.active_client_name
      ? focus.data.active_project_name
        ? `${focus.data.active_client_name} · ${focus.data.active_project_name}`
        : focus.data.active_client_name
      : null) ??
    PHASE_SHORT[phase] ??
    'Work block';

  return (
    <div
      className={cn(
        'material fixed inset-x-0 z-chrome border-t',
        'bottom-[var(--tab-bar-height,0px)]',
        'pb-[var(--focus-bar-pad,max(0.5rem,env(safe-area-inset-bottom,0px)))]',
      )}
    >
      <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-2">
        <span aria-hidden className="size-dot shrink-0 animate-pulse rounded-full bg-accent" />

        <div className="min-w-0 flex-1">
          <p className="truncate text-small font-medium">{title}</p>
          <p className="truncate text-label text-text-tertiary">
            {PHASE_SHORT[phase] ?? phase}
            {(focus.data.active_task_id ||
              focus.data.active_calendar_event_id ||
              focus.data.active_client_id) && (
              <span className="tabular">
                {' '}
                · {formatClock(Math.max(taskElapsed, focus.data.task_elapsed_seconds))}
              </span>
            )}
          </p>
        </div>

        <span
          className={cn(
            'tabular shrink-0 text-body font-medium',
            showOverage && 'text-warning',
          )}
        >
          {showOverage
            ? `+${formatCountdown(overage)}`
            : endsAt
              ? formatCountdown(remaining)
              : '-'}
        </span>

        <button
          type="button"
          onClick={onExpand}
          aria-label="Open work block"
          title="Open work block"
          className={cn(
            'pressable flex size-action shrink-0 items-center justify-center rounded-full',
            'bg-accent text-text-on-accent hover:bg-accent-hover',
          )}
        >
          <Maximize2 size={15} strokeWidth={1.5} aria-hidden />
        </button>
      </div>
    </div>
  );
}
